import { DrawerSection, DrawerTimeline } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Check, Pause, Circle, X, Ban } from 'lucide-react';

/**
 * Step states, mirroring the prototype's trace glyphs: done ✓, paused ⏸,
 * error ✗, blocked ⊘, pending ·.
 */
const STATE_META = {
  done: { icon: Check, variant: 'green' },
  paused: { icon: Pause, variant: 'amber' },
  error: { icon: X, variant: 'red' },
  blocked: { icon: Ban, variant: 'red' },
  pending: { icon: Circle, variant: 'neutral' },
};

/**
 * `DrawerTimeline` ships 32px nodes and `space-y-6`, which is roomier than the
 * prototype's trace. Tightening it from the outside keeps `packages/ui` untouched;
 * the smaller node also centres better on the connector, which sits at 15px.
 */
const TIMELINE_DENSITY =
  '[&>ul]:space-y-4 [&_li>div:first-child]:size-6 [&_li>div:first-child_svg]:size-3';

/**
 * Workflow trace up to the paused node. Two hosts:
 *   - inline in an event body (`section` default) — HTL Continuation (CT-4859)
 *     and HTL Handoff (CT-4860);
 *   - inside {@link TraceSlideOut} with `section={false}`, where the panel header
 *     already carries the title and a second "Workflow Trace" heading would be
 *     redundant.
 */
function WorkflowTraceList({ steps = [], reference, title, section = true }) {
  const { t } = useTranslation('work-queues-app');
  if (!steps.length) return null;

  const heading = [reference, title].filter(Boolean).join(' · ');

  const items = steps.map((step, index) => {
    const { icon, variant } = STATE_META[step.state] ?? STATE_META.pending;
    return {
      key: step.id ?? `${index}-${step.label}`,
      icon,
      variant,
      title: (
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-content-faint">
            {t('detail.step', { number: index + 1 })}
          </span>
          <span className={step.state === 'pending' ? 'text-content-muted' : undefined}>
            {step.label}
          </span>
        </span>
      ),
      description: step.description,
      timestamp: step.timestamp,
    };
  });

  const body = (
    <div className="flex flex-col gap-3">
      {heading ? <p className="text-sm font-medium text-content">{heading}</p> : null}
      <DrawerTimeline items={items} className={TIMELINE_DENSITY} />
    </div>
  );

  if (!section) return body;
  return <DrawerSection title={t('detail.workflowTrace')}>{body}</DrawerSection>;
}

export default WorkflowTraceList;
